/**
 * MesPiecesEnfance — LISTE UNIFIÉE des pièces côté administré (enfance).
 *
 * Une SEULE liste, simple et universelle : on ne distingue pas « scolaire » vs
 * « petite enfance » pour le parent. Le backend (`pieces-requises`) calcule
 * automatiquement les pièces RÉELLEMENT demandées (activées par la mairie dans
 * GRC) selon l'usage réel de la famille. Toute pièce listée est OBLIGATOIRE.
 *
 * Une pièce déposée une fois sert tous les dossiers (bibliothèque famille).
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge } from '@/components/ui';
import { api } from '@/lib/api';
import type { PieceRequise } from '@/lib/api';
import { FileText, Upload, Download, Check, X, Clock } from 'lucide-react';

export function MesPiecesEnfance({
  familleId,
  setError,
}: {
  familleId: string;
  setError: (s: string | null) => void;
}) {
  const [pieces, setPieces] = useState<PieceRequise[]>([]);
  const [fichiers, setFichiers] = useState<Record<string, { id: string; nomFichier: string; version: number } | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Liste unifiée des pièces requises + bibliothèque (pour le téléchargement).
      const [req, lib] = await Promise.all([
        api.famillePieces.piecesRequises(familleId),
        api.famillePieces.list(familleId),
      ]);
      setPieces(req.pieces);
      const map: Record<string, { id: string; nomFichier: string; version: number }> = {};
      for (const f of lib.pieces) map[f.pieceTypeId] = { id: f.id, nomFichier: f.nomFichier, version: f.version };
      setFichiers(map);
    } catch {
      setError('Impossible de charger vos pièces.');
    } finally {
      setLoading(false);
    }
  }, [familleId, setError]);

  useEffect(() => { void load(); }, [load]);

  const fournies = pieces.filter((p) => p.dejaFournie).length;
  const manquantes = pieces.filter((p) => !p.dejaFournie).length;

  const deposer = async (pieceTypeId: string, file: File) => {
    setBusy(pieceTypeId); setError(null);
    try {
      await api.famillePieces.deposer(familleId, pieceTypeId, file);
      await load();
    } catch {
      setError('Le dépôt a échoué (formats acceptés : PDF, JPG, PNG).');
    } finally { setBusy(null); }
  };

  const telecharger = async (pieceId: string) => {
    try {
      const { url } = await api.famillePieces.url(familleId, pieceId);
      window.open(url, '_blank', 'noopener');
    } catch { setError('Téléchargement impossible.'); }
  };

  if (loading) {
    return <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>;
  }

  if (pieces.length === 0) {
    return (
      <Card variant="glass" padded>
        <div className="py-6 text-center text-sm text-ink-subtle">
          Aucune pièce n'est demandée pour le moment.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
        <Badge variant="success" size="sm">{fournies} fournie{fournies > 1 ? 's' : ''}</Badge>
        {manquantes > 0 && <Badge variant="warning" size="sm">{manquantes} à fournir</Badge>}
        <span className="text-ink-subtle">Déposez chaque pièce une fois : elle sert tous vos dossiers.</span>
      </div>

      <div className="space-y-2">
        {pieces.map((p) => {
          const fichier = fichiers[p.pieceTypeId];
          return (
            <Card key={p.pieceTypeId} variant="glass" className="p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-surface/60">
                  <FileText className="h-4.5 w-4.5 text-ink-muted" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink">{p.libelle}</span>
                    <span className="text-[11px] text-ink-subtle">(obligatoire)</span>
                  </div>
                  {p.dejaFournie && fichier ? (
                    <div className="text-[11px] text-ink-subtle">
                      {fichier.nomFichier} · v{fichier.version}
                      {p.conforme === true && ' · conforme'}
                      {p.conforme === false && ' · non conforme'}
                      {p.conforme == null && ' · en attente de vérification'}
                    </div>
                  ) : (
                    <div className="text-[11px] text-ink-subtle">Non fournie</div>
                  )}
                </div>

                <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                  {p.dejaFournie ? (
                    <>
                      {p.conforme === true
                        ? <Badge variant="success" size="sm"><Check className="mr-0.5 h-3 w-3" aria-hidden="true" />OK</Badge>
                        : p.conforme === false
                          ? <Badge variant="urgent" size="sm"><X className="mr-0.5 h-3 w-3" aria-hidden="true" />Refusée</Badge>
                          : <Badge variant="warning" size="sm"><Clock className="mr-0.5 h-3 w-3" aria-hidden="true" />À vérifier</Badge>}
                      {fichier && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => void telecharger(fichier.id)}>
                          <Download className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      )}
                      <FileInput label="Remplacer" busy={busy === p.pieceTypeId}
                        onPick={(f) => void deposer(p.pieceTypeId, f)} />
                    </>
                  ) : (
                    <FileInput label="Déposer" busy={busy === p.pieceTypeId}
                      onPick={(f) => void deposer(p.pieceTypeId, f)} primary />
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/** Bouton d'upload déclenchant un <input type=file> caché. */
function FileInput({
  label, busy, primary, onPick,
}: {
  label: string;
  busy: boolean;
  primary?: boolean;
  onPick: (file: File) => void;
}) {
  const id = `fi-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <label htmlFor={id}>
      <input
        id={id} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="sr-only"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.currentTarget.value = ''; }}
      />
      <Button type="button" variant={primary ? 'cantine' : 'secondary'} size="sm" disabled={busy}
        onClick={(e) => { e.preventDefault(); (e.currentTarget.previousElementSibling as HTMLInputElement)?.click(); }}>
        <Upload className="h-3.5 w-3.5" aria-hidden="true" /> {busy ? '…' : label}
      </Button>
    </label>
  );
}
